import { Task, FullProject } from '../../../types';
import { mergeProjectTasks } from '../../../utils/taskConflictResolver';
import { areTaskListsEqual } from '../../../utils/taskComparison';
import { isCommProject, isSystemProject, extractRawTaskId } from '../../../utils/idNormalizer';

export interface ProjectFilterScope {
  userId?: string;
  tenantId: string;
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
}

/**
 * Pure filter predicate determining whether a Firestore project document
 * belongs to the current user/tenant scope.
 */
export function shouldAcceptProjectSnapshot(
  fp: FullProject,
  scope: ProjectFilterScope
): boolean {
  const { userId, tenantId, INBOX_PROJECT_ID, COMM_PROJECT_ID } = scope;
  if (fp.id.startsWith('inbox_inforum_')) {
    if (fp.id !== INBOX_PROJECT_ID && fp.id !== 'inbox_inforum_' + tenantId) return false;
  }
  if (fp.id.startsWith('comm_internal_')) {
    if (fp.id !== COMM_PROJECT_ID) return false;
  }
  if (fp.id.startsWith('sample_') || fp.isSample || fp.metadata?.isSample) {
    if (fp.userId && fp.userId !== userId) return false;
    if (!userId || !fp.id.includes(userId)) return false;
  }
  return true;
}

/**
 * Determines which project IDs require a project-scoped `where('projectId', 'in', chunk)`
 * task listener or query so both collaborators AND project owners receive tasks created
 * by other editors (`userId !== ownerId`) in real time.
 */
export function getCollaborativeProjectIds(
  projects: FullProject[],
  userId?: string,
  INBOX_PROJECT_ID?: string
): string[] {
  const ids = new Set<string>();
  for (const p of projects) {
    if (!p?.id) continue;
    if (INBOX_PROJECT_ID && p.id === INBOX_PROJECT_ID) continue;
    if (p.id.startsWith('sample_') || p.isSample || p.metadata?.isSample) continue;

    const isForeignProject = Boolean(userId && p.userId !== userId);
    const hasSharedViewers = Array.isArray(p.sharedWith) && p.sharedWith.length > 0;
    const hasSharedEditors = Array.isArray(p.sharedEditors) && p.sharedEditors.length > 0;
    const isPublic = p.visibility === 'public';

    if (isForeignProject || hasSharedViewers || hasSharedEditors || isPublic) {
      ids.add(p.id);
    }
  }
  return Array.from(ids);
}

/**
 * Groups flat Firestore tasks into their respective normalized project IDs,
 * ensuring pool contacts stay in COMM_PROJECT_ID while project Vorgänge stay in their target project.
 */
export function groupRawTasksByProject(
  rawProjects: FullProject[],
  rawTasks: Task[],
  options: {
    COMM_PROJECT_ID: string;
    normalizeProjectId: (id?: string) => string | undefined;
  }
): Map<string, Task[]> {
  const { COMM_PROJECT_ID, normalizeProjectId } = options;
  const tasksByProjectId = new Map<string, Task[]>();

  rawProjects.forEach(proj => {
    const normId = normalizeProjectId(proj.id) || proj.id;
    tasksByProjectId.set(normId, []);
  });

  rawTasks.forEach(t => {
    let pId = normalizeProjectId(t.projectId) || 'default';
    const isSampleContactRecord =
      String(t.id).startsWith('person_sample_contact_') ||
      (String(t.id).startsWith('sample_contact_') && t.type === 'contact');
    const isPersonMaster = t.type === 'contact' && t.status === 'pool';
    const isCommItem =
      t.type === 'contact' ||
      t.category === 'Kommunikation' ||
      String(t.id).startsWith('sample_contact_') ||
      Boolean(t.contactId) ||
      Boolean(t.personKey);
    const realProjectFromIds = Array.isArray(t.projectIds)
      ? t.projectIds.find(pid => pid && !isSystemProject(pid))
      : undefined;

    if (!isPersonMaster && (!t.projectId || isSystemProject(t.projectId)) && realProjectFromIds) {
      const candidatePid = normalizeProjectId(realProjectFromIds) || realProjectFromIds;
      if (tasksByProjectId.has(candidatePid)) {
        pId = candidatePid;
      }
    }

    const isGlobalComm =
      (!pId || isSystemProject(pId) || !tasksByProjectId.has(pId)) &&
      isCommItem;
    if (isPersonMaster || isSampleContactRecord || isGlobalComm) {
      pId = normalizeProjectId(COMM_PROJECT_ID) || COMM_PROJECT_ID;
    }

    if (!tasksByProjectId.has(pId)) {
      tasksByProjectId.set(pId, []);
    }
    tasksByProjectId
      .get(pId)!
      .push(pId !== t.projectId && !isPersonMaster && !isCommProject(pId) ? { ...t, projectId: pId } : t);
  });

  return tasksByProjectId;
}

export interface BuildMergedCloudProjectsOptions {
  tasksLoaded: boolean;
  migratedLegacyProjectIds: Set<string>;
  recentlyDeletedTaskIds: Set<string>;
  COMM_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  onMigrateLegacyProject?: (projectWithTasks: FullProject) => void;
}

/**
 * Combines raw cloud project documents with grouped cloud tasks, normalizes custom completed columns,
 * and sorts tasks by sortOrder.
 */
export function buildMergedCloudProjects(
  rawProjects: FullProject[],
  rawTasks: Task[],
  options: BuildMergedCloudProjectsOptions
): FullProject[] {
  const {
    tasksLoaded,
    migratedLegacyProjectIds,
    recentlyDeletedTaskIds,
    COMM_PROJECT_ID,
    normalizeProjectId,
    onMigrateLegacyProject,
  } = options;

  const tasksByProjectId = groupRawTasksByProject(rawProjects, rawTasks, {
    COMM_PROJECT_ID,
    normalizeProjectId,
  });

  return rawProjects.map(proj => {
    let projTasks = proj.tasks || [];
    const normId = normalizeProjectId(proj.id) || proj.id;

    if (tasksLoaded) {
      const normalizedTasks = tasksByProjectId.get(normId) || [];
      if (normalizedTasks.length > 0) {
        projTasks = normalizedTasks;
      } else if (
        proj.tasks &&
        proj.tasks.length > 0 &&
        !migratedLegacyProjectIds.has(normId) &&
        !isCommProject(normId) &&
        normId !== 'inbox_inforum'
      ) {
        projTasks = proj.tasks.filter(t => !recentlyDeletedTaskIds.has(String(t.id)));
        migratedLegacyProjectIds.add(normId);
        if (projTasks.length > 0 && onMigrateLegacyProject) {
          onMigrateLegacyProject({ ...proj, tasks: projTasks });
        }
      } else {
        projTasks = [];
      }
    }

    const customCols = proj.metadata?.customColumns || [];
    const completedColumnIds = new Set(
      customCols
        .filter(c => c.category === 'completed')
        .map(c => c.id)
    );
    completedColumnIds.add('completed');

    const mappedTasks = projTasks.map(t => {
      const tStatus = t.status || (t.completed ? 'completed' : 'open');
      const shouldBeCompleted = completedColumnIds.has(tStatus);
      if (shouldBeCompleted && !t.completed) {
        return { ...t, completed: true, status: tStatus };
      }
      return t;
    });

    const sortedTasks = [...mappedTasks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    return {
      ...proj,
      id: normId,
      rawId: proj.rawId || proj.id,
      tasks: sortedTasks,
    } as FullProject;
  });
}

export interface ReconcileProjectsOptions {
  dirtyProjectIds: Set<string>;
  syncingProjectIds: Set<string>;
  dirtyTaskIds: Set<string>;
  taskBaselines: Map<string, Task>;
  recentlyDeletedTaskIds: Set<string>;
  syncedProjectTimestamps: Map<string, number>;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
}

/**
 * Reconciles the current local projects array (`prev`) with incoming cloud projects (`mergedProjects`),
 * protecting local dirty edits, preventing stale cloud snapshots from overwriting newer local state,
 * and returning the exact `prev` reference if nothing materially changed.
 */
export function reconcileLocalAndCloudProjects(
  prev: FullProject[],
  mergedProjects: FullProject[],
  options: ReconcileProjectsOptions
): FullProject[] {
  const {
    dirtyProjectIds: dirtyIds,
    syncingProjectIds: syncingIds,
    dirtyTaskIds,
    taskBaselines,
    recentlyDeletedTaskIds,
    syncedProjectTimestamps,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
  } = options;

  const next = [...prev];

  mergedProjects.forEach(fp => {
    const idx = next.findIndex(p => (p.rawId || p.id) === (fp.rawId || fp.id) || p.id === fp.id);
    if (idx === -1) {
      next.push(fp);
    } else {
      const currentProj = next[idx];
      const normalizedFpId = normalizeProjectId(fp.id);
      const hasDirtyTaskInProject =
        (currentProj.tasks || []).some(t => dirtyTaskIds.has(String(t.id)) || Boolean(t.isInlineNew)) ||
        (fp.tasks || []).some(t => dirtyTaskIds.has(String(t.id)));
      const isDirty =
        dirtyIds.has(fp.id) ||
        (fp.rawId && dirtyIds.has(fp.rawId)) ||
        (normalizedFpId && dirtyIds.has(normalizedFpId)) ||
        syncingIds.has(fp.id) ||
        (fp.rawId && syncingIds.has(fp.rawId)) ||
        (normalizedFpId && syncingIds.has(normalizedFpId)) ||
        hasDirtyTaskInProject;

      if (isDirty) {
        const mergedTasks = mergeProjectTasks(
          currentProj.tasks || [],
          fp.tasks || [],
          dirtyTaskIds,
          taskBaselines,
          recentlyDeletedTaskIds
        );

        (fp.tasks || []).forEach(t => {
          const idStr = String(t.id);
          if (!dirtyTaskIds.has(idStr)) {
            taskBaselines.set(idStr, t);
          }
        });

        next[idx] = {
          ...currentProj,
          tasks: mergedTasks,
          metadata: currentProj.metadata || fp.metadata,
        };
        return;
      }

      const latestLocalModified = Math.max(
        currentProj.lastModified || 0,
        syncedProjectTimestamps.get(fp.id) || 0,
        (fp.rawId ? syncedProjectTimestamps.get(fp.rawId) : 0) || 0,
        (normalizedFpId ? syncedProjectTimestamps.get(normalizedFpId) : 0) || 0
      );

      if (latestLocalModified && fp.lastModified && fp.lastModified < latestLocalModified) {
        return;
      }

      if (latestLocalModified && fp.lastModified && fp.lastModified === latestLocalModified) {
        if (!areTaskListsEqual(fp.tasks, currentProj.tasks)) {
          const currentHasSort = currentProj.tasks.length > 0 && typeof currentProj.tasks[0].sortOrder === 'number';
          const fpHasSort = fp.tasks.length > 0 && typeof fp.tasks[0].sortOrder === 'number';
          if (currentHasSort && !fpHasSort && fp.tasks.length === currentProj.tasks.length) {
            return;
          }

          if (fp.tasks.length < currentProj.tasks.length) {
            const currentTaskIds = new Set(currentProj.tasks.map(t => String(t.id)));
            const fpTaskIds = new Set(fp.tasks.map(t => String(t.id)));
            const missingIds = Array.from(currentTaskIds).filter(id => !fpTaskIds.has(id));
            const allMissingWereDeleted =
              missingIds.length > 0 &&
              missingIds.every(
                id => recentlyDeletedTaskIds.has(id) || recentlyDeletedTaskIds.has(extractRawTaskId(id))
              );

            if (!allMissingWereDeleted) {
              return;
            }
          }
        }
      }

      (fp.tasks || []).forEach(t => {
        taskBaselines.set(String(t.id), t);
      });
      next[idx] = fp;
    }
  });

  const cloudIds = new Set(mergedProjects.map(p => p.id));
  cloudIds.add(INBOX_PROJECT_ID);
  cloudIds.add(normalizeProjectId(COMM_PROJECT_ID) || COMM_PROJECT_ID);
  const filtered = next.filter(p => p.id.startsWith('local_') || cloudIds.has(p.id));

  const hasChanges =
    filtered.length !== prev.length ||
    filtered.some(p => {
      const prevP = prev.find(old => old.id === p.id || old.rawId === p.id || old.id === p.rawId);
      if (!prevP) {
        return true;
      }
      if (p === prevP) {
        return false;
      }
      if (
        (p.id !== prevP.id && p.id !== prevP.rawId && p.rawId !== prevP.id) ||
        p.name !== prevP.name ||
        p.color !== prevP.color ||
        p.workspace !== prevP.workspace ||
        !p.archived !== !prevP.archived ||
        p.order !== prevP.order ||
        p.categoryId !== prevP.categoryId
      ) {
        return true;
      }
      if (p.metadata !== prevP.metadata && JSON.stringify(p.metadata || {}) !== JSON.stringify(prevP.metadata || {})) {
        return true;
      }
      if (p.changelog !== prevP.changelog && JSON.stringify(p.changelog || []) !== JSON.stringify(prevP.changelog || [])) {
        return true;
      }
      if (p.tasks !== prevP.tasks && !areTaskListsEqual(p.tasks, prevP.tasks)) {
        return true;
      }
      return false;
    });

  if (!hasChanges) {
    return prev;
  }

  return filtered;
}
